import { useState } from "react";

import { SortingOrder } from "src/graphql/anti-fraud/__generated__/graphql.ts";

export type SortOptions<TFields extends string> = Partial<Record<TFields, SortingOrder>>;

export type SelectedSortOptions<TFields extends string> = {
  field: keyof SortOptions<TFields>;
  order: SortingOrder;
};

export const useSort = <TFields extends string>(defaultOptions?: SortOptions<TFields>) => {
  const [selectedSortOption, setSelectedSortOption] = useState<
    SelectedSortOptions<TFields> | undefined
  >(() => {
    if (defaultOptions) {
      const entries = Object.entries(defaultOptions);
      if (entries.length > 0) {
        const [field, order] = entries[0]; // Get the first criterion
        return {
          field: field as keyof SortOptions<TFields>,
          order: (order as SortingOrder) || "ASC",
        };
      }
    }
  });

  const isSortOptionSelected = (key: keyof SortOptions<TFields>): boolean =>
    (selectedSortOption && selectedSortOption.field === key) ?? false;

  const getSortOptionOrder = (): SortingOrder | undefined => {
    if (selectedSortOption) {
      return selectedSortOption.order;
    }
    return undefined;
  };

  const setSortOption = (key: keyof SortOptions<TFields>, sortingOrder?: SortingOrder): void => {
    if (sortingOrder !== undefined) {
      setSelectedSortOption({ field: key, order: sortingOrder });
    } else {
      setSelectedSortOption(undefined);
    }
  };

  const clearSelectedSortOption = (): void => {
    setSelectedSortOption(undefined);
  };

  return {
    selectedSortOption,
    isSortOptionSelected,
    getSortOptionOrder,
    setSortOption,
    clearSelectedSortOption,
  };
};
